import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, Loader2, ArrowRight, AlertTriangle, FolderKanban, ShoppingCart,
  ListChecks, FileSignature, Receipt, ShieldCheck, FilePlus2, ClipboardCheck,
  Package, CheckSquare, CalendarDays, FileText,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { Badge } from '@/components/common';
import {
  cautaInDosare, esteAnulare, mesajEroareCautare, CAUTARE_MIN_CARACTERE,
} from '@/services/search.service';
import type {
  AcoperireCamp, FiltruTipRezultat, RaspunsCautare, RezultatCautare, TipRezultat,
} from '@/types';

// ============================================================================
//  Căutarea în Dosarul Investiției — overlay-ul (Ctrl+K) și piesele comune.
//
//  Fișierul acesta ține, pe lângă overlay, piesele pe care le folosește identic
//  și pagina `/cautare`: registrul de tipuri, construcția rutei, evidențierea,
//  rândul de rezultat, starea de gol și hook-ul de căutare. Sunt într-un singur
//  fișier ca să existe O SINGURĂ definiție a fiecăreia — azi în aplicație
//  existau două căutări aproape identice (aceasta și cea inline din Header),
//  iar a treia copie ar fi fost răspunsul greșit la problema greșită.
//
//  Ce NU face nimic de aici: nu verifică drepturi (backendul le aplică în
//  interogare) și nu oferă descărcare de fișiere (răspunsul dă doar
//  `atasamentId`, fără numele de pe disc, iar ruta de descărcare are o problemă
//  de autentificare semnalată de autorul backendului).
// ============================================================================

// ---------------------------------------------------------------------------
//  Registrul de tipuri — oglinda registrului de câmpuri din backend.
//  Un CÂMP nou (din cele 126 prevăzute în DOC_CAMPURI_LIPSA) nu se vede aici
//  deloc: apare în `potriviri[]` și se randează generic. Un TIP nou = o linie.
// ---------------------------------------------------------------------------
export interface DefinitieTip {
  eticheta: string;          // titlul grupului, la plural
  etichetaSingular: string;
  iconita: LucideIcon;
}

export const REGISTRU_TIPURI: Record<TipRezultat, DefinitieTip> = {
  proiect: { eticheta: 'Proiecte', etichetaSingular: 'Proiect', iconita: FolderKanban },
  achizitie: { eticheta: 'Achiziții', etichetaSingular: 'Achiziție', iconita: ShoppingCart },
  etapa_achizitie: { eticheta: 'Etape de achiziție', etichetaSingular: 'Etapă de achiziție', iconita: ListChecks },
  contract: { eticheta: 'Contracte', etichetaSingular: 'Contract', iconita: FileSignature },
  factura: { eticheta: 'Facturi', etichetaSingular: 'Factură', iconita: Receipt },
  garantie: { eticheta: 'Garanții', etichetaSingular: 'Garanție', iconita: ShieldCheck },
  act_aditional: { eticheta: 'Acte adiționale', etichetaSingular: 'Act adițional', iconita: FilePlus2 },
  receptie: { eticheta: 'Recepții', etichetaSingular: 'Recepție', iconita: ClipboardCheck },
  livrabil: { eticheta: 'Livrabile', etichetaSingular: 'Livrabil', iconita: Package },
  activitate: { eticheta: 'Activități', etichetaSingular: 'Activitate', iconita: CheckSquare },
  eveniment_implementare: { eticheta: 'Evenimente de implementare', etichetaSingular: 'Eveniment de implementare', iconita: CalendarDays },
  document_achizitie: { eticheta: 'Documente de achiziție', etichetaSingular: 'Document de achiziție', iconita: FileText },
  document_contract: { eticheta: 'Documente de contract', etichetaSingular: 'Document de contract', iconita: FileText },
  document_livrabil: { eticheta: 'Documente de livrabil', etichetaSingular: 'Document de livrabil', iconita: FileText },
  document_activitate: { eticheta: 'Documente de activitate', etichetaSingular: 'Document de activitate', iconita: FileText },
  document_implementare: { eticheta: 'Documente de implementare', etichetaSingular: 'Document de implementare', iconita: FileText },
};

// Backendul poate primi o entitate nouă în registru fără ca frontendul să fie
// republicat. Atunci `REGISTRU_TIPURI[tip]` ar fi `undefined` și randarea ar
// crăpa — deci lookup-ul trece mereu pe aici.
export function definitieTip(tip: string): DefinitieTip {
  return REGISTRU_TIPURI[tip as TipRezultat] ?? {
    eticheta: 'Alte rezultate',
    etichetaSingular: 'Rezultat',
    iconita: FileText,
  };
}

// Eticheta filtrului `?tip=document`, meta-valoarea acceptată de endpoint.
export const ETICHETA_TIP_DOCUMENT = 'Documente (toate)';

export function etichetaFiltruTip(tip: FiltruTipRezultat): string {
  return tip === 'document' ? ETICHETA_TIP_DOCUMENT : definitieTip(tip).eticheta;
}

// ---------------------------------------------------------------------------
//  Ruta. Backendul trimite ancoră semantică (tab + entitate + subsecțiune),
//  nu URL: o redenumire de rută nu trebuie să ceară republicarea backendului.
//  Traducerea în URL se face DOAR aici.
// ---------------------------------------------------------------------------
const TABURI_PROIECT = new Set([
  'details', 'achizitii', 'contracte', 'livrabile', 'implementare', 'activitati',
  'gantt', 'files', 'members',
]);

export function caleRezultat(rezultat: RezultatCautare): string {
  const proiectId = rezultat.proiect?.id ?? rezultat.ancora?.proiectId;
  if (rezultat.tip === 'proiect') return `/projects/${proiectId}`;

  const ancora = rezultat.ancora;
  // Un tab necunoscut ar duce la o filă goală; `details` există întotdeauna.
  const tab = ancora && TABURI_PROIECT.has(ancora.tab) ? ancora.tab : 'details';

  // `dosar`/`sub`/`rand` sunt inerte astăzi: ProjectDetailPage citește doar
  // hash-ul (VALID_TABS). Se trimit totuși, pentru că sunt exact contractul din
  // planul de cablare a secțiunilor, iar până atunci navigarea se degradează
  // elegant la nivel de filă — nu se strică nimic dacă nu sunt citite.
  const params = new URLSearchParams();
  if (ancora?.entitate && ancora?.entitateId) params.set('dosar', `${ancora.entitate}-${ancora.entitateId}`);
  if (ancora?.subsectiune) params.set('sub', ancora.subsectiune);
  if (ancora?.randId) params.set('rand', String(ancora.randId));
  const sir = params.toString();
  return `/projects/${proiectId}${sir ? `?${sir}` : ''}#${tab}`;
}

// ---------------------------------------------------------------------------
//  Gruparea. Backendul trimite un vector PLAT, deja sortat după scor. Grupăm în
//  ordinea primei apariții și păstrăm ordinea din grup, ca primul rând vizibil
//  să rămână, prin construcție, cel mai bun rezultat — și ca ordinea de la
//  tastatură să fie identică cu cea de pe ecran.
// ---------------------------------------------------------------------------
export interface GrupRezultate {
  tip: string;
  eticheta: string;
  rezultate: RezultatCautare[];
}

export function grupeazaRezultate(rezultate: RezultatCautare[]): GrupRezultate[] {
  const grupuri: GrupRezultate[] = [];
  const dupaTip = new Map<string, GrupRezultate>();
  for (const rezultat of rezultate) {
    let grup = dupaTip.get(rezultat.tip);
    if (!grup) {
      grup = { tip: rezultat.tip, eticheta: definitieTip(rezultat.tip).eticheta, rezultate: [] };
      dupaTip.set(rezultat.tip, grup);
      grupuri.push(grup);
    }
    grup.rezultate.push(rezultat);
  }
  return grupuri;
}

// ---------------------------------------------------------------------------
//  Evidențierea. Offset-urile vin de la backend, în unități UTF-16 peste `text`
//  exact așa cum e trimis (cu diacritice). La potrivirea trigram interogarea nu
//  apare ca subșir, deci un indexOf local ar întoarce -1 tocmai acolo unde
//  evidențierea e obligatorie (decizia 8).
//  Bucățile se construiesc ca noduri React — niciodată dangerouslySetInnerHTML:
//  textul vine din nume de fișiere încărcate de utilizatori.
// ---------------------------------------------------------------------------
export function EvidentiazaFragment({
  text,
  offsets,
}: {
  text: string;
  offsets?: Array<[number, number]> | null;
}) {
  if (!text) return null;
  const perechi = (offsets ?? [])
    .filter((o) => Array.isArray(o) && o.length === 2 && o[0] >= 0 && o[1] > o[0] && o[0] < text.length)
    .sort((a, b) => a[0] - b[0]);
  if (perechi.length === 0) return <>{text}</>;

  const bucati: React.ReactNode[] = [];
  let cursor = 0;
  perechi.forEach(([s, e], i) => {
    const start = Math.max(s, cursor);
    const end = Math.min(e, text.length);
    if (end <= start) return; // intervale suprapuse sau ieșite din text
    if (start > cursor) bucati.push(<React.Fragment key={`t${i}`}>{text.slice(cursor, start)}</React.Fragment>);
    bucati.push(
      <mark key={`m${i}`} className="bg-warning-100 text-inherit rounded-sm px-0.5">
        {text.slice(start, end)}
      </mark>
    );
    cursor = end;
  });
  if (cursor < text.length) bucati.push(<React.Fragment key="rest">{text.slice(cursor)}</React.Fragment>);
  return <>{bucati}</>;
}

// ---------------------------------------------------------------------------
//  Rândul de rezultat (decizia 8: decizia se ia FĂRĂ click).
//  Identificator cu fragmentul potrivit evidențiat · contextul care distinge
//  (linia pre-compusă de backend) · ÎNTOTDEAUNA proiectul.
//  Nu e <button>: un buton interactiv într-un listbox e ARIA invalid. Activarea
//  se prinde pe onMouseDown, înaintea lui blur.
// ---------------------------------------------------------------------------
export function RandRezultat({
  rezultat,
  selectat,
  idElement,
  onActivare,
  onIndicare,
  refElement,
}: {
  rezultat: RezultatCautare;
  selectat: boolean;
  idElement: string;
  onActivare: (rezultat: RezultatCautare) => void;
  onIndicare?: () => void;
  refElement?: (element: HTMLDivElement | null) => void;
}) {
  const definitie = definitieTip(rezultat.tip);
  const Iconita = definitie.iconita;
  const potrivire = rezultat.potriviri?.[0];
  // Offset-urile aparțin câmpului potrivit, care de multe ori NU e titlul
  // („Contractant", „Fișier atașat"). Se evidențiază peste titlu doar când e
  // vorba de exact același text; altfel marcajul ar caterisi alte litere.
  const potrivireInTitlu = !!potrivire && potrivire.text === rezultat.titlu;
  const arataPotrivireSeparat = !!potrivire && !potrivireInTitlu;
  const fisierLipsa = rezultat.context?.areFisier === false;

  return (
    <div
      id={idElement}
      ref={refElement}
      role="option"
      aria-selected={selectat}
      onMouseDown={(e) => {
        e.preventDefault();
        onActivare(rezultat);
      }}
      onMouseEnter={onIndicare}
      className={clsx(
        'flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors',
        selectat ? 'bg-primary-50' : 'hover:bg-gray-50'
      )}
    >
      <Iconita
        className={clsx('w-5 h-5 mt-0.5 flex-shrink-0', selectat ? 'text-primary-600' : 'text-gray-400')}
        aria-hidden="true"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="font-medium text-gray-900 truncate">
            {potrivireInTitlu ? (
              <EvidentiazaFragment text={rezultat.titlu} offsets={potrivire?.offsets} />
            ) : (
              rezultat.titlu
            )}
          </p>
          {rezultat.nivel === 'aproximativ' && (
            <Badge size="sm" variant="default" className="flex-shrink-0">
              potrivire aproximativă
            </Badge>
          )}
          {fisierLipsa && (
            <Badge size="sm" variant="warning" className="flex-shrink-0">
              Fișier indisponibil
            </Badge>
          )}
        </div>

        {arataPotrivireSeparat && (
          <p className="text-xs text-gray-600 truncate">
            <span className="text-gray-400">{potrivire.eticheta}: </span>
            <EvidentiazaFragment text={potrivire.text} offsets={potrivire.offsets} />
          </p>
        )}

        {rezultat.subtitlu && (
          <p className="text-sm text-gray-500 truncate">{rezultat.subtitlu}</p>
        )}
      </div>

      {/* Proiectul, mereu. „Factura 2026-245" nu înseamnă nimic într-un
          portofoliu de 42 de investiții. Excepție: rândul CHIAR E proiectul. */}
      {rezultat.tip !== 'proiect' && rezultat.proiect && (
        <span
          title={rezultat.proiect.identificator}
          className="flex-shrink-0 max-w-[12rem] truncate text-xs text-gray-600 bg-gray-100 rounded-full px-2 py-1"
        >
          {rezultat.proiect.nume}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Starea de gol (decizia 7): nu „niciun rezultat", ci gradul de acoperire —
//  în CE s-a căutat și cât e completat. Cifrele vin din răspuns (backendul le
//  taie pe proiectele vizibile), nu sunt scrise de mână aici.
//  Efect secundar dorit: omul vede că necompletarea îl costă.
// ---------------------------------------------------------------------------
const MAX_CAMPURI_ACOPERIRE = 8;

function adunaAcoperire(acoperire: AcoperireCamp[]): Array<{ eticheta: string; completate: number; total: number; fel: string }> {
  // Aceeași etichetă apare la mai multe tipuri („Fișier" la facturi, garanții,
  // acte adiționale). Se adună, altfel lista ar fi cinci rânduri identice.
  const dupaEticheta = new Map<string, { eticheta: string; completate: number; total: number; fel: string }>();
  for (const camp of acoperire) {
    const cheie = camp.eticheta;
    const existent = dupaEticheta.get(cheie);
    if (existent) {
      existent.completate += camp.completate;
      existent.total += camp.total;
    } else {
      dupaEticheta.set(cheie, {
        eticheta: camp.eticheta,
        completate: camp.completate,
        total: camp.total,
        fel: camp.fel,
      });
    }
  }
  return Array.from(dupaEticheta.values()).sort((a, b) => {
    if ((a.fel === 'identificator') !== (b.fel === 'identificator')) return a.fel === 'identificator' ? -1 : 1;
    return b.total - a.total;
  });
}

export function StareGoala({
  raspuns,
  interogare,
  compact = false,
}: {
  raspuns: RaspunsCautare | null;
  interogare: string;
  compact?: boolean;
}) {
  const preaScurt = interogare.trim().length < CAUTARE_MIN_CARACTERE;

  if (preaScurt || !raspuns) {
    return (
      <div className={clsx('text-center text-gray-500', compact ? 'px-6 py-8' : 'py-12')}>
        <p className="text-gray-700 font-medium mb-2">Căutați în dosarele investițiilor</p>
        <p className="text-sm">
          Un număr de factură („2026-245"), un număr sau o denumire de contract, o denumire de
          achiziție sau de livrabil, un nume de document.
        </p>
        <p className="text-sm mt-2">Puteți scrie și o propoziție: cuvintele de umplutură se aruncă.</p>
      </div>
    );
  }

  const campuri = adunaAcoperire(raspuns.acoperire ?? []);
  const aratate = campuri.slice(0, MAX_CAMPURI_ACOPERIRE);
  const ramase = campuri.length - aratate.length;
  const jetoane = raspuns.jetoane ?? [];
  // Se arată doar când s-a curățat ceva: altfel omul crede că a fost ignorat.
  const arataJetoane = jetoane.length > 0 && jetoane.join(' ') !== interogare.trim().toLowerCase();
  // Cazul în care propoziția a rămas fără niciun jeton („dă-mi toate"): backendul
  // nu are ce căuta și nici cifre de acoperire nu întoarce. Fără explicație,
  // pare că aplicația a ignorat cererea.
  const totulEUmplutura = jetoane.length === 0 && interogare.trim().length >= CAUTARE_MIN_CARACTERE;

  return (
    <div className={clsx('text-gray-600', compact ? 'px-6 py-6' : 'py-10')}>
      <p className="text-gray-900 font-medium">
        Nu am găsit nimic pentru «{raspuns.interogare || interogare}».
      </p>
      {arataJetoane && (
        <p className="text-sm mt-1">
          Am căutat după:{' '}
          {jetoane.map((jeton) => (
            <code key={jeton} className="bg-gray-100 rounded px-1 mx-0.5 text-gray-800">
              {jeton}
            </code>
          ))}
        </p>
      )}
      {totulEUmplutura && (
        <p className="text-sm mt-1">
          Toate cuvintele scrise sunt cuvinte de legătură. Scrieți un număr sau o denumire
          („2026-245", „ocolitoare Mizil", „caiet de sarcini").
        </p>
      )}
      {aratate.length > 0 && (
        <div className="mt-3 text-sm">
          <p className="mb-1">Am căutat în, din proiectele la care aveți acces:</p>
          <ul className="space-y-0.5">
            {aratate.map((camp) => (
              <li key={camp.eticheta} className="flex items-center gap-2">
                <span className="text-gray-700">{camp.eticheta}</span>
                <span
                  className={clsx(
                    'text-xs',
                    camp.completate === 0 ? 'text-danger-600' : 'text-gray-500'
                  )}
                >
                  ({camp.completate} din {camp.total} completate)
                </span>
              </li>
            ))}
          </ul>
          {ramase > 0 && (
            <p className="text-xs text-gray-500 mt-1">și încă {ramase} câmpuri.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Rândul de eroare. Azi eroarea de căutare dispare într-un console.error, iar
//  omul vede „nu s-au găsit rezultate" pentru o cerere care a PICAT — cel mai
//  prost mesaj cu putință, fiindcă sugerează că datele nu există.
// ---------------------------------------------------------------------------
export function RandEroare({ mesaj, onReincercare }: { mesaj: string; onReincercare: () => void }) {
  return (
    <div className="m-3 flex items-start gap-3 rounded-lg border border-danger-100 bg-danger-50 px-4 py-3">
      <AlertTriangle className="w-5 h-5 text-danger-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-danger-600">{mesaj}</p>
      </div>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onReincercare}
        className="text-sm font-medium text-danger-600 underline hover:no-underline"
      >
        Încearcă din nou
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Hook-ul de căutare: debounce 300 ms + AbortController + stare.
//  Folosit identic de overlay și de pagină, ca să nu existe două comportamente
//  diferite pentru aceeași căutare.
// ---------------------------------------------------------------------------
export interface OptiuniCautare {
  tip?: FiltruTipRezultat | null;
  proiect?: number | null;
  activ?: boolean;
  intarziere?: number;
}

export interface StareCautare {
  raspuns: RaspunsCautare | null;
  seIncarca: boolean;
  eroare: string | null;
  reincarca: () => void;
}

export function useCautare(interogare: string, optiuni: OptiuniCautare = {}): StareCautare {
  const { tip = null, proiect = null, activ = true, intarziere = 300 } = optiuni;
  const [raspuns, setRaspuns] = useState<RaspunsCautare | null>(null);
  const [seIncarca, setSeIncarca] = useState(false);
  const [eroare, setEroare] = useState<string | null>(null);
  const [reluari, setReluari] = useState(0);

  const q = interogare.trim();

  useEffect(() => {
    if (!activ || q.length < CAUTARE_MIN_CARACTERE) {
      setRaspuns(null);
      setEroare(null);
      setSeIncarca(false);
      return;
    }

    const controller = new AbortController();
    setSeIncarca(true);

    const ceas = setTimeout(async () => {
      try {
        const rezultat = await cautaInDosare({ q, tip, proiect }, controller.signal);
        setRaspuns(rezultat);
        setEroare(null);
      } catch (err) {
        // Anularea nu e eroare: la fiecare tastă anulăm cererea anterioară.
        if (esteAnulare(err)) return;
        setEroare(mesajEroareCautare(err));
        setRaspuns(null);
      } finally {
        // La anulare nu stingem indicatorul: cererea nouă e deja pe drum, iar
        // o clipire între „se caută" și „gata" ar face lista să sară.
        if (!controller.signal.aborted) setSeIncarca(false);
      }
    }, intarziere);

    return () => {
      clearTimeout(ceas);
      controller.abort();
    };
  }, [q, tip, proiect, activ, intarziere, reluari]);

  const reincarca = useCallback(() => setReluari((n) => n + 1), []);

  return { raspuns, seIncarca, eroare, reincarca };
}

// Mesajul citit de cititoarele de ecran. Se actualizează după ce căutarea s-a
// așezat, nu la fiecare tastă.
export function anuntRezultate(raspuns: RaspunsCautare | null, seIncarca: boolean): string {
  if (seIncarca) return '';
  if (!raspuns) return '';
  if (raspuns.total === 0) return 'Niciun rezultat.';
  const grupuri = grupeazaRezultate(raspuns.rezultate).length;
  const cuvant = raspuns.total === 1 ? 'rezultat' : 'rezultate';
  const categorii = grupuri === 1 ? 'o categorie' : `${grupuri} categorii`;
  return `${raspuns.trunchiat ? 'Cel puțin ' : ''}${raspuns.total} ${cuvant} în ${categorii}.`;
}

// ============================================================================
//  OVERLAY-UL (Ctrl+K)
//
//  Poate fi montat în două feluri:
//    <SearchModal />                        — își ține singur starea și
//                                             înregistrează Ctrl+K global;
//    <SearchModal isOpen={x} onClose={f} /> — controlat de părinte (Layout),
//                                             care ține și scurtătura.
//  Ambele variante există pentru că montarea și butonul din Header aparțin
//  altui fișier; overlay-ul nu trebuie să depindă de ordinea în care sosesc.
// ============================================================================

// Câte rezultate se arată pe grup în overlay. Restul se numără în subsol și se
// văd în pagină — overlay-ul e pentru „știu ce caut", nu pentru „vreau tot".
const MAX_PE_GRUP_OVERLAY = 6;

interface SearchModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

// Un rând din indexul plat de tastatură: fie un rezultat, fie ultimul rând
// („vezi toate"). Indexul se construiește din ORDINEA DE RANDARE, altfel
// săgețile s-ar desincroniza de ce e pe ecran.
type RandNavigabil =
  | { fel: 'rezultat'; cheie: string; rezultat: RezultatCautare }
  | { fel: 'toate'; cheie: string };

export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const navigate = useNavigate();
  const controlat = isOpen !== undefined;
  const [deschisIntern, setDeschisIntern] = useState(false);
  const deschis = controlat ? !!isOpen : deschisIntern;

  const inchide = useCallback(() => {
    if (controlat) onClose?.();
    else setDeschisIntern(false);
  }, [controlat, onClose]);

  const [interogare, setInterogare] = useState('');
  const [indexSelectat, setIndexSelectat] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const focusAnterior = useRef<HTMLElement | null>(null);
  // `cheie` conține două puncte, deci nu se caută cu querySelector.
  const randuriRef = useRef(new Map<string, HTMLElement>());

  const { raspuns, seIncarca, eroare, reincarca } = useCautare(interogare, { activ: deschis });

  // Scurtătura globală: doar în varianta necontrolată. Dacă părintele ține
  // starea, tot el ține și scurtătura — altfel s-ar declanșa de două ori.
  useEffect(() => {
    if (controlat) return;
    const laTasta = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setDeschisIntern((d) => !d);
      }
    };
    document.addEventListener('keydown', laTasta);
    return () => document.removeEventListener('keydown', laTasta);
  }, [controlat]);

  // Reset la deschidere + focus. useLayoutEffect, nu setTimeout(...,100): cu
  // temporizator există un cadru în care câmpul nu are focus și prima literă
  // tastată se pierde.
  useLayoutEffect(() => {
    if (!deschis) return;
    focusAnterior.current = document.activeElement as HTMLElement | null;
    setInterogare('');
    setIndexSelectat(0);
    inputRef.current?.focus();
  }, [deschis]);

  // Restaurarea focusului la închidere: fără ea, utilizatorul de tastatură e
  // aruncat la începutul paginii.
  useEffect(() => {
    if (deschis) return;
    const anterior = focusAnterior.current;
    focusAnterior.current = null;
    if (anterior && typeof anterior.focus === 'function' && document.contains(anterior)) {
      anterior.focus();
    }
  }, [deschis]);

  const grupuri = useMemo(
    () => grupeazaRezultate(raspuns?.rezultate ?? []),
    [raspuns]
  );

  // Ce se randează efectiv (maximum 6 pe grup) și, din asta, indexul plat.
  const { grupuriAfisate, ascunse } = useMemo(() => {
    let ascunseAcum = 0;
    const afisate = grupuri.map((grup) => {
      ascunseAcum += Math.max(0, grup.rezultate.length - MAX_PE_GRUP_OVERLAY);
      return { ...grup, rezultate: grup.rezultate.slice(0, MAX_PE_GRUP_OVERLAY) };
    });
    return { grupuriAfisate: afisate, ascunse: ascunseAcum };
  }, [grupuri]);

  const total = raspuns?.total ?? 0;
  const trunchiat = raspuns?.trunchiat === true;
  const arataToate = total > 0;

  const randuri = useMemo<RandNavigabil[]>(() => {
    const lista: RandNavigabil[] = [];
    for (const grup of grupuriAfisate) {
      for (const rezultat of grup.rezultate) {
        lista.push({ fel: 'rezultat', cheie: rezultat.cheie, rezultat });
      }
    }
    if (arataToate) lista.push({ fel: 'toate', cheie: 'cautare-toate' });
    return lista;
  }, [grupuriAfisate, arataToate]);

  // Indexul plat, căutat după cheie: la randare fiecare rând are nevoie de
  // poziția lui, iar un findIndex per rând ar fi pătratic.
  const indexDupaCheie = useMemo(() => {
    const harta = new Map<string, number>();
    randuri.forEach((rand, i) => harta.set(rand.cheie, i));
    return harta;
  }, [randuri]);

  // Un răspuns nou = selecția se întoarce pe primul rând (cel mai bun).
  useEffect(() => {
    setIndexSelectat(0);
  }, [raspuns]);

  // Derularea la elementul activ: lipsea, iar la 16 tipuri lista depășește sigur
  // înălțimea maximă.
  useEffect(() => {
    const rand = randuri[indexSelectat];
    if (!rand) return;
    randuriRef.current.get(rand.cheie)?.scrollIntoView({ block: 'nearest' });
  }, [indexSelectat, randuri]);

  const activeaza = useCallback(
    (rand: RandNavigabil) => {
      // Am navigat: focusul aparține paginii noi. Fără linia asta, efectul de
      // restaurare de mai jos ar rula DUPĂ montarea paginii de căutare și i-ar
      // smulge focusul din câmp înapoi la butonul din Header.
      focusAnterior.current = null;
      if (rand.fel === 'toate') {
        navigate(`/cautare?q=${encodeURIComponent(interogare.trim())}`);
      } else {
        navigate(caleRezultat(rand.rezultat));
      }
      inchide();
    },
    [navigate, interogare, inchide]
  );

  // Enter se prinde DOAR pe câmp. Pe container ar fi o greșeală: `preventDefault`
  // pe keydown-ul unui <button> suprimă click-ul sintetizat, deci „golește
  // câmpul" și „încearcă din nou" n-ar mai putea fi apăsate de la tastatură.
  const laTastaInput = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const rand = randuri[indexSelectat];
      if (rand) activeaza(rand);
    },
    [randuri, indexSelectat, activeaza]
  );

  // Capcană de focus: Tab circulă doar în interiorul overlay-ului. Modal.tsx nu
  // o are, de aceea overlay-ul nu îl refolosește.
  const laTastaContainer = useCallback((e: React.KeyboardEvent) => {
    // Escape ȘI săgețile se prind pe container, nu doar pe câmp: focusul poate
    // ajunge prin Tab pe „golește câmpul" sau pe „încearcă din nou", iar acolo
    // săgețile nu ar mai muta selecția și, nefiind oprite, ar derula pagina din
    // spatele dialogului. Câmpul nu le mai tratează, deci nu se dublează.
    if (e.key === 'Escape') {
      e.preventDefault();
      inchide();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndexSelectat((i) => (randuri.length === 0 ? 0 : (i + 1) % randuri.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndexSelectat((i) => (randuri.length === 0 ? 0 : (i - 1 + randuri.length) % randuri.length));
      return;
    }
    if (e.key !== 'Tab') return;
    const focusabile = containerRef.current?.querySelectorAll<HTMLElement>(
      'input, button, [href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusabile || focusabile.length === 0) return;
    const primul = focusabile[0];
    const ultimul = focusabile[focusabile.length - 1];
    if (e.shiftKey && document.activeElement === primul) {
      e.preventDefault();
      ultimul.focus();
    } else if (!e.shiftKey && document.activeElement === ultimul) {
      e.preventDefault();
      primul.focus();
    }
  }, [inchide, randuri.length]);

  if (!deschis) return null;

  const q = interogare.trim();
  const randSelectat = randuri[indexSelectat];
  const arataStareaGoala = !seIncarca && !eroare && (q.length < CAUTARE_MIN_CARACTERE || total === 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" onClick={inchide} />

      <div className="relative min-h-screen flex items-start justify-center pt-[15vh] px-4">
        <div
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-label="Căutare în dosarele investițiilor"
          onKeyDown={laTastaContainer}
          className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden"
        >
          {/* Câmpul */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
            <Search className="w-5 h-5 text-gray-400" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={randuri.length > 0}
              aria-controls="rezultate-cautare-overlay"
              aria-autocomplete="list"
              aria-activedescendant={randSelectat ? `overlay-${randSelectat.cheie}` : undefined}
              aria-label="Căutare în dosarele investițiilor"
              value={interogare}
              onChange={(e) => setInterogare(e.target.value)}
              onKeyDown={laTastaInput}
              placeholder="Caută în dosarele investițiilor…"
              className="flex-1 bg-transparent text-gray-900 placeholder-gray-500 focus:outline-none"
            />
            {seIncarca ? (
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" aria-hidden="true" />
            ) : interogare ? (
              <button
                type="button"
                onClick={() => {
                  setInterogare('');
                  inputRef.current?.focus();
                }}
                aria-label="Golește câmpul"
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            ) : (
              <kbd className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-500">ESC</kbd>
            )}
          </div>

          {/* Rezultate */}
          <div className="max-h-[60vh] overflow-y-auto">
            {eroare && <RandEroare mesaj={eroare} onReincercare={reincarca} />}

            <div id="rezultate-cautare-overlay" role="listbox" aria-label="Rezultatele căutării">
              {grupuriAfisate.map((grup) => (
                <div key={grup.tip} role="group" aria-labelledby={`grup-overlay-${grup.tip}`} className="p-2">
                  <p
                    id={`grup-overlay-${grup.tip}`}
                    className="px-3 py-1 text-xs font-medium text-gray-500 uppercase"
                  >
                    {grup.eticheta}
                  </p>
                  {grup.rezultate.map((rezultat) => {
                    const index = indexDupaCheie.get(rezultat.cheie) ?? -1;
                    return (
                      <RandRezultat
                        key={rezultat.cheie}
                        rezultat={rezultat}
                        selectat={index === indexSelectat}
                        idElement={`overlay-${rezultat.cheie}`}
                        refElement={(el) => {
                          if (el) randuriRef.current.set(rezultat.cheie, el);
                          else randuriRef.current.delete(rezultat.cheie);
                        }}
                        onIndicare={() => setIndexSelectat(index)}
                        onActivare={(r) => activeaza({ fel: 'rezultat', cheie: r.cheie, rezultat: r })}
                      />
                    );
                  })}
                </div>
              ))}

              {arataToate && (
                <div
                  id="overlay-cautare-toate"
                  role="option"
                  aria-selected={randSelectat?.fel === 'toate'}
                  ref={(el) => {
                    if (el) randuriRef.current.set('cautare-toate', el);
                    else randuriRef.current.delete('cautare-toate');
                  }}
                  onMouseEnter={() => setIndexSelectat(randuri.length - 1)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    activeaza({ fel: 'toate', cheie: 'cautare-toate' });
                  }}
                  className={clsx(
                    'flex items-center gap-2 px-5 py-3 border-t border-gray-100 cursor-pointer text-sm',
                    randSelectat?.fel === 'toate' ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'
                  )}
                >
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  {ascunse > 0 || trunchiat
                    ? `Vezi toate rezultatele${trunchiat ? '' : ` (${total})`}`
                    : 'Deschide rezultatele în pagină'}
                </div>
              )}
            </div>

            {arataStareaGoala && (
              <StareGoala raspuns={raspuns} interogare={interogare} compact />
            )}
          </div>

          {/* Anunț pentru cititoarele de ecran */}
          <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {anuntRezultate(raspuns, seIncarca)}
          </div>

          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↓</kbd>
              pentru navigare
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↵</kbd>
              pentru deschidere
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">ESC</kbd>
              pentru închidere
            </span>
            {trunchiat && (
              <span className="ml-auto text-warning-600">
                Se arată primele {total} rezultate.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchModal;
